import { createSelector } from 'reselect';
import { getRouteArray } from 'app/utilities/url';

const getPage = (state, url) => {
    const routeArray = getRouteArray(url);

    return state.pages.getIn(routeArray);
};

const pageSelector = createSelector([getPage], (page) => {
    if (page && page.getIn(['data', 0])) {
        return page.getIn(['data', 0]).toJS();
    }

    return null;
});

/**
 * News collections are returned alongside the entity, e.g. `featured_news`.
 * Items are reshaped into the `{ id, type, data }` form the article tiles and
 * `EntityLink` expect.
 *
 * @param {Immutable.Map} page
 * @param {string} key Response key holding the collection
 *
 * @return {array|undefined} news items
 */
const getNewsCollection = (page, key) => {
    const items = page && page.getIn([key, 'data']);

    if (!items) {
        return undefined;
    }

    return items
        .filter((item) => Boolean(item.get('attributes')))
        .map((item) => ({
            id: item.get('id'),
            type: item.get('type'),
            data: item.get('attributes').toJS()
        }))
        .toJS();
};

export const featuredNewsSelector = createSelector([getPage], (page) => getNewsCollection(page, 'featured_news'));

export const latestNewsSelector = createSelector([getPage], (page) => getNewsCollection(page, 'latest_news'));

export const popularNewsSelector = createSelector([getPage], (page) => getNewsCollection(page, 'popular_news'));

/**
 * Categories are returned alongside the entity, reshaped to just what the
 * category selector needs.
 *
 * @param {Immutable.Map} page
 * @param {string} key Response key holding the categories
 *
 * @return {array|undefined} categories
 */
const getCategoryCollection = (page, key) => {
    const categories = page && page.getIn([key, 'data']);

    if (!categories) {
        return undefined;
    }

    return categories.map((category) => ({
        id: category.get('id'),
        name: category.getIn(['attributes', 'name']),
        slug: category.getIn(['attributes', 'slug']),
        thumbnail: category.getIn(['attributes', 'thumbnail'])
    })).toJS();
};

// Every level-1 category. Returned by both the `/news` landing page and a
// level-1 category page, which lists them all and highlights the current one.
export const newsCategoriesSelector = createSelector([getPage], (page) => getCategoryCollection(page, 'news_categories'));

// The children of a level-1 category, only present on its own page
export const newsSubCategoriesSelector = createSelector([getPage], (page) => getCategoryCollection(page, 'subcategories'));

export default pageSelector;
